import { getLocaleStrings } from "survey-creator-core";
export const enStrings = {
  languages: {
    title: "Languages",
    navigationTitle: "Languages",
    description: "Select the language of the Survey Creator UI and target\nlanguages for the survey being configured.",
    creatorUI: "Survey Creator UI language",
    defaultSurveyLocale: "Default survey language",
    surveyLanguages: "Survey languages",
    searchPlaceholder: "Type to search...",
    translateToEnglish: "Translate language names to English"
  },
  tabs: {
    title: "Tabs",
    navigationTitle: "Tabs",
    editItemTitle: "Edit Tab",
    description: "Select the tabs you want to show, reorder them, change their titles,\nand choose the tab that will be active by default.",
    items: "Visible tabs",
    hiddenItems: "Hidden tabs",
    defaultTab: "Default tab",
    noItemsText: "Drag tabs here to show them",
    noHiddenItemsText: "Drag tabs here to hide them",
    name: "Name",
    titleField: "Title",
    iconName: "Icon name"
  },
  toolbox: {
    title: "Toolbox",
    navigationTitle: "Toolbox",
    editItemTitle: "Edit Toolbox Item",
    createItemTitle: "Create Toolbox Item",
    editCategoryTitle: "Edit Toolbox Category",
    createCategoryTitle: "Create Toolbox Category",
    description: "Select the toolbox items you want to show, group them into categories, \nrename them, and change their order if required.",
    toolbox: "Toolbox",
    categories: "Categories",
    noCategoriesText: "No categories",
    noItemsText: "Drag items here to show them",
    noHiddenItemsText: "Drag items here to hide them",
    hiddenItems: "Hidden toolbox items",
    enableGrouping: "Enable grouping",
    showCategoryTitles: "Show category titles",
    categoryName: "Name",
    categoryTitle: "Title",
    tooltip: "Tooltip",
    iconName: "Icon name",
    jsonObject: "JSON object to apply when users select this toolbox item",
    subitems: "Subitems",
    enableSubitems: "Enable subitems for this question",
    moveAsSubitem: "Move as a subitem to...",
    addToToolbox: "Add to Toolbox",
    deleteCustomItem: "Delete Custom Item",
    addCustomCategory: "New Category",
    addCustomItem: "New Item",
    addNewItem: "Add new item",
    addNewSubitem: "Add a new subitem",
    noRowsText: "There are no items",
    newItem: "New item in",
  },
  propertyGrid: {
    title: "Property Grid",
    navigationTitle: "Property Grid",
    editItemTitle: "Edit Property",
    createCategoryTitle: "Create Property Grid Category",
    editCategoryTitle: "Edit Property Grid Category",
    description: "Hide, rename, reorder, or change property grouping in the Property Grid.",
    selectElement: "Select a survey element to customize",
    propertyGrid: "Property Grid",
    noCategoriesText: "No categories",
    noItemsText: "Drag properties here to show them",
    noHiddenItemsText: "Drag properties here to hide them",
    hiddenItems: "Hidden properties",
    categoryName: "Name",
    categoryTitle: "Title",
    iconName: "Icon name",
    name: "Name",
    titleField: "Title",
    descriptionField: "Helper text",
    propertyVisibleIn: "Property visible in",
    addCustomCategory: "New Category"
  },
  options: {
    title: "Options",
    navigationTitle: "Options",
    description: "Detailed configuration settings for Survey Creator.",
    o: {
      pageEditMode: "Page edit mode",
      showSurveyHeader: "Show survey header",
      showHeaderInEmptySurvey: "Show header when survey is empty",
      useElementTitles: "Show element titles instead of IDs",
      allowDragPages: "Enable page drag-and-drop",
      allowZoom: "Enable zoom controls",
      expandCollapseButtonVisibility: "Show expand/collapse buttons",
      collapseQuestions: "Collapse questions by default",
      collapsePanels: "Collapse panels by default",
      collapsePages: "Collapse pages by default",
      maxRows: "Maximum number of matrix rows",
      maxColumns: "Maximum number of matrix columns",
      minChoices: "Minimum number of choices",
      maxChoices: "Maximum number of choices",
      maxVisibleChoices: "Maximum number of visible choices",
      maxRateValues: "Maximum number of rating values",
      forbiddenNestedElementsPanel: "Elements not allowed inside a Panel",
      forbiddenNestedElementsPanelDynamic: "Elements not allowed inside a Dynamic Panel",
      maxPanelNestingLevel: "Maximum panel nesting depth",
      maxChoiceContentNestingLevel: "Maximum nesting depth for choice content",
      previewAllowSimulateDevices: "Enable survey preview on different devices",
      previewDevice: "Default device for preview",
      previewOrientation: "Preview orientation",
      previewAllowSelectLanguage: "Show language selector",
      previewAllowSelectPage: "Show page selector",
      previewAllowHiddenElements: "Show toggle for hidden elements",
      logicAllowTextEditExpressions: "Allow editing expressions as plain text",
      logicMaxItemsInCondition: "Maximum number of items in a logic condition",
      clearTranslationsOnSourceTextChange: "Clear translations when source text changes",
      expressionsValidateFunctions: "Validate variables and element names in expressions",
      expressionsValidateVariables: "Validate functions in expressions",
      expressionsValidateSyntax: "Validate expression syntax",
    },
    ov: {
      pageEditMode: {
        "standard": "Scrollable pages",
        "bypage": "Page-by-page editing",
        "single": "Single page only"
      },
      expandCollapseButtonVisibility: {
        "never": "Never",
        "always": "Always",
        "onhover": "On hover"
      },
      previewOrientation: {
        "landscape": "Landscape (Horizontal)",
        "portrait": "Portrait (Vertical)"
      },
      previewAllowSelectLanguage: {
        "auto": "Only for multilingual surveys",
        "true": "Always (all available languages)",
        "all": "Always (translated languages when available)",
        "false": "Never",
      }
    },
    ohelp: {
      pageEditMode: 'Choose how pages are navigated in the Designer tab. "Scrollable pages" - survey designers can scroll to move between multiple pages; "Page-by-page editing" - survey designers switch between pages using the page menu; "Single page only" - all questions are placed on a single page, and page creation and deletion are disabled.',
      showSurveyHeader: "Controls whether survey designers can see and edit the survey header and its properties.",
      showHeaderInEmptySurvey: "Controls whether the survey title and description appear on the design surface when the survey has no questions or panels.",
      useElementTitles: "Controls whether Survey Creator displays titles instead of IDs for survey elements across the UI, including the survey tree, conditional logic, validation rules, etc.",
      allowDragPages: "Controls whether survey designers can drag and drop pages to reorder them on the design surface.",
      allowZoom: "Controls whether the Zoom In, Zoom Out, and Zoom to 100% buttons are available.",
      maxRows: "Specifies the number of rows that survey designers can add to Single-Select Matrix and Multi-Select Matrix questions.",
      maxColumns: "Specifies the number of columns that survey designers can add to Single-Select Matrix, Multi-Select Matrix, and Dynamic Matrix questions.",
      minChoices: "Specifies the minimum number of choices allowed in Checkboxes, Dropdown, Radio Button Group, and other choice questions. Survey designers won't be able to delete choices below this limit.",
      maxChoices: "Specifies the maximum number of choices survey designers can add to Checkboxes, Dropdown, Radio Button Group, and other choice questions.",
      maxVisibleChoices: "Specifies the maximum number of choices shown. Survey designers can click “Show more” to view hidden choices. Set to -1 to show all choices.",
      maxRateValues: "Specifies the maximum number of rating values that survey designers can add to Rating Scale questions.",
      maxPanelNestingLevel: "Specifies the maximum nesting level for Panels and Dynamic Panels. Use -1 for no limit, 0 to allow nesting only questions, or a positive number to set the maximum nesting depth.",
      maxChoiceContentNestingLevel: "Specifies how many levels of follow-up questions can be nested within choice options in Radio Button Group and Checkboxes questions. Use 0 to disable nesting, 1 to allow first-level choice options to contain nested content, 2 to allow first- and second-level choice options to contain nested content, and so on. Higher values enable deeper nesting levels.",
      previewAllowSimulateDevices: "Controls whether the Preview tab shows the Device button for testing surveys on different devices.",
      previewAllowSelectLanguage: 'Choose whether the Preview tab shows the language selector. "Only for multilingual surveys" - show the selector only if the survey has translations; "Always (translated languages when available)" - always show the selector, listing only survey translations when present and all available languages otherwise; "Always (all available languages)" - always show the selector with all available languages; "Never" - never show the selector.',
      previewAllowSelectPage: "Choose whether the Preview tab shows the page selector at the bottom. When enabled, survey designers can freely navigate between pages, ignoring validation rules.",
      previewAllowHiddenElements: "Specifies whether the Preview tab shows a toogle (eye icon) to show or hide invisible survey elements.",
      logicAllowTextEditExpressions: "Controls whether survey designers can manually edit logical expressions in the Logic tab as plain text instead of using the visual editor.",
      logicMaxItemsInCondition: "-1 is unlimited.",
      clearTranslationsOnSourceTextChange: "Specifies whether translations in other languages are cleared when the source language text is changed.",
      expressionsValidateFunctions: "When selected, Survey Creator reports an error if an expression references an unknown variable or survey element name.",
      expressionsValidateVariables: "When selected, Survey Creator reports an error if an expression uses an unknown function.",
      expressionsValidateSyntax: "When selected, Survey Creator reports an error if an expression contains syntax errors.",
    }
  },
  items: {
    addNewItem: "Add new item",
    addNewSubitem: "Add new subitem",
    newItem: "New item in",
    addCustomCategory: "New Category",
    newCategory: "New category in",
    moveToCategory: "Move to category...",
    moveToNewCategory: "Move to new category",
    categoriesLabel: "Categories",
    moveTo: "Move To...",
    removeFrom: "Remove from {0}",
    more: "More",
    restoreToDefault: "Restore",
    edit: "Edit",
    expand: "Expand",
    collapse: "Collapse",
    remove: "Remove",
    add: "Add",
    delete: "Delete"
  },
  editor: {
    title: "Creator Presets",
    presetJson: "Preset JSON:",
    copy: "Copy",
    download: "Download",
    load: "Load",
    saveExit: "Save & Exit",
    back: "Back",
    resetToDefault: "Reset",
    resetToDefaults: "All settings restored to default",
    resoredToDefault: "resored to default",
    itemRestoredToDefault: "Item restored to default",
    categoryRestoredToDefault: "Category restored to default",
    edit: "Edit",
    completeText: "Save & Exit",
    pagePrevText: "Back",
    required: "Please enter a value.",
    protectedPresetName: "This name is reserved for a built-in preset. Please choose a different name.",
  },
  plugin: {
    buttonTitle: "UI Preset Editor",
    buttonDescription: "Configure the Toolbox, Tabs, Property Grid, and other UI elements, and save the setup as a reusable preset.",
    presetsTab: "Presets",
    creatorPresets: "Creator Presets",
    save: "Save",
    discarded: "All changes discarded",
    title: "Title",
    template: "Template",
    add: "Add",
    discard: "Discard",
    cancel: "Cancel",
    saveAs: "Save as...",
    saveAsTitle: "Save Preset As...",
    createNewPreset: "Create \"{0}\" preset",
    quit: "Quit",
    quitConfirmation: "Return to Survey Creator?",
    quitConfirmationMessage: "You will lose all unsaved changes.",
    quitConfirmationSave: "Save and exit",
    quitConfirmationDiscard: "Exit without saving",
    deleteConfirmation: "Delete preset \"{0}\"?",
    deleteConfirmationMessage: "This action cannot be undone.",
    deleteConfirmationOk: "Delete",
    deleteConfirmationCancel: "Cancel",
    switchPresetConfirmation: "Switch to another preset?",
    switchPresetConfirmationMessage: "You will lose all unsaved changes.",
    switchPresetConfirmationOk: "Switch preset",
    switchPresetConfirmationCancel: "Cancel",
    importConfirmation: "Import a new preset?",
    importConfirmationMessage: "You will lose all unsaved changes.",
    importConfirmationOk: "Import",
    importConfirmationCancel: "Cancel",

    file: "File",
    import: "Import",
    export: "Export",
    edit: "Edit",
    resetLanguages: "Reset Languages",
    resetAll: "Discard all changes",
    resetConfirmation: "Discard all changes?",
    resetConfirmationMessage: "You will lose all unsaved changes",
    resetConfirmationOk: "Discard changes",
    resetConfirmationCancel: "Cancel",

    status: {
      saved: "Saved",
      unsaved: "Unsaved changes",
      saving: "Saving...",
      initial: "Initial"
    },

    resetToDefaults: "Reset {0}",
    defaultSettings: "Default Settings",
    basic: "Basic",
    advanced: "Advanced",
    expert: "Expert",
    defaultConfiguration: "Default configuration",
    selectPresetPlaceholder: "Select a preset...",
    editPresetsList: "Manage presets...",
    editPresetsListTitle: "Manage Presets",
    addNewPreset: "Add new preset",
    addNewPresetTitle: "Create Preset",
    deletePreset: "Delete",
    savedPresets: "Saved presets",
    license: "<a href='https://surveyjs.io/' target='_blank'>UI Preset Editor</a> is available only with a PRO license. To use it in your application, please <a href='https://surveyjs.io/manage#renewals-and-upgrades' target='_blank'>upgrade your license</a>.",
    license2: "Your maintenance subscription expired on {date}. You may continue using all <a href='https://surveyjs.io/stay-updated/release-notes' target='_blank'>versions released up to that date</a>. To remove this banner in the latest version, please <a href='https://surveyjs.io/manage#license-manager' target='_blank'>renew your subscription</a> and <a href='https://surveyjs.io/remove-alert-banner' target='_blank'>set up a new license key</a>. Please note <a href='https://surveyjs.io/' target='_blank'>UI Preset Editor</a> is available to all users with an active PRO license.",
    licenseCreator: "To use the Survey Creator plugin in your application, a <a href='https://surveyjs.io/licensing' target='_blank'>developer license</a> is required. If you have an active license, please <a href='https://surveyjs.io/remove-alert-banner' target='_blank'>set up your license key</a> and ensure you're using the <a href='https://surveyjs.io/stay-updated/release-notes' target='_blank'>latest version</a>. Please note <a href='https://surveyjs.io/' target='_blank'>UI Preset Editor</a> is included only in the PRO license tier.",
    licenseCreator2: "Your maintenance subscription expired on {date}. You may continue using all <a href='https://surveyjs.io/stay-updated/release-notes' target='_blank'>versions released up to that date</a>. To remove this banner in the latest version, please <a href='https://surveyjs.io/manage#license-manager' target='_blank'>renew your subscription</a> and <a href='https://surveyjs.io/remove-alert-banner' target='_blank'>set up a new license key</a>. Please note <a href='https://surveyjs.io/' target='_blank'>UI Preset Editor</a> is included only in the PRO license tier. To use it in your application, please also <a href='https://surveyjs.io/manage#renewals-and-upgrades' target='_blank'>upgrade your license</a>."
  }
};
const locale = getLocaleStrings("en");
locale.presets = enStrings;